import React from "react";
import {
  Activity, Camera, Flame, Music, Target, Timer, Ruler, History,
  TrendingUp, Sparkles,
} from "lucide-react";
import { BentoCard, Eyebrow, WordHeading, MEAL_IMG, TRAIN_IMG } from "./_shared";

export default function BentoSection() {
  return (
    <section id="features" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <Eyebrow>Capabilities</Eyebrow>
            <WordHeading
              headingClass="font-display text-5xl sm:text-6xl max-w-3xl leading-[0.95] tracking-tight"
              tokens={[
                { w: "Every" },
                { w: "system" },
                { w: "you" },
                { w: "need.", mr: "0" },
                { br: true },
                { w: "Zero",        className: "text-white/50" },
                { w: "compromises.", className: "text-white/50", mr: "0" },
              ]}
            />
          </div>
          <p className="text-white/60 max-w-md text-base">
            Forget juggling MyFitnessPal, a workout PDF, a notes app, and a Spotify tab.
            DAIONE fuses it all into a single, AI-driven control panel.
          </p>
        </div>

        {/* Bento grid — asymmetric tetris */}
        <div className="grid grid-cols-1 md:grid-cols-8 gap-5">
          <BentoCard
            index={0}
            className="md:col-span-5 md:row-span-2 min-h-[420px] dark-island"
            bgImage={TRAIN_IMG}
            bgPosition="right center"
            Icon={Activity}
            eyebrow="AI Workout Engine"
            title="Periodized plans built around you."
            desc="Tell us your goal, gear, and weekly availability. Claude designs a multi-week progression with sets, reps, rest, and weekly deload checks. Adjust week-by-week as you adapt."
            bullets={[
              "1–12 week multi-cycle programs",
              "Auto-adjusts to fitness level + equipment",
              "Progressive overload baked in",
              "Personal records detected automatically",
            ]}
            trace
            testId="bento-workout"
          />

          <BentoCard
            index={1}
            className="md:col-span-3 md:row-span-2 min-h-[420px] dark-island"
            bgImage={MEAL_IMG}
            bgPosition="center"
            Icon={Camera}
            iconClass="text-[#7C3AED] border-[#7C3AED]/40"
            eyebrow="Photo Meal Logging"
            title="Snap your plate. Done."
            desc="Claude Vision reads any meal photo and returns calories, protein, carbs, and fat — with confidence ratings. Editable before save. Faster than barcode scanning."
            bullets={[
              "On-device image resize (1280px JPEG)",
              "Confidence + assumption notes",
              "Manual edit before logging",
            ]}
            testId="bento-photo-meal"
          />

          <BentoCard
            index={2}
            className="md:col-span-3"
            Icon={Sparkles}
            iconClass="text-[#6B9BFF] border-[#6B9BFF]/40"
            eyebrow="AI Food Estimator"
            title="Type what you ate."
            desc='"2 scrambled eggs + sourdough toast" → calories and macros in 2 seconds.'
            testId="bento-text-food"
          />

          <BentoCard
            index={3}
            className="md:col-span-3"
            Icon={Flame}
            iconClass="text-[#FF3B30] border-[#FF3B30]/40"
            eyebrow="Muscle Heatmap"
            title="See what's under-trained."
            desc="A weekly muscle map shows where you've been slacking. Tap any area — Claude builds an AI micro-workout in seconds."
            testId="bento-heatmap"
          />

          <BentoCard
            index={4}
            className="md:col-span-2"
            Icon={Music}
            iconClass="text-[#1DB954] border-[#1DB954]/40"
            eyebrow="Spotify"
            title="Soundtrack baked in."
            desc="Persistent music pill — your tracks keep playing as you navigate."
            testId="bento-spotify"
          />

          <BentoCard
            index={5}
            className="md:col-span-3"
            Icon={Target}
            iconClass="text-[#6B9BFF] border-[#6B9BFF]/40"
            eyebrow="Goals & Auto-TDEE"
            title="Macros that adapt."
            desc="Weight or lift goals auto-compute TDEE, daily kcal target, and protein/carb/fat split — recalibrating as your body changes."
            testId="bento-goals"
          />

          <BentoCard
            index={6}
            className="md:col-span-3"
            Icon={Ruler}
            iconClass="text-[#7C3AED] border-[#7C3AED]/40"
            eyebrow="DAIONE"
            title="Centimeters > Scale."
            desc="Track chest, waist, hips, and arms over time. Trend charts and deltas surface progress the bathroom scale hides."
            testId="bento-measurements"
          />

          <BentoCard
            index={7}
            className="md:col-span-2"
            Icon={Timer}
            iconClass="text-[#FFB020] border-[#FFB020]/40"
            eyebrow="Hold Timer"
            title="Auto-rest, multi-set."
            desc="Static holds with auto-rest loops. No phone-juggling mid-plank."
            testId="bento-timer"
          />

          <BentoCard
            index={8}
            className="md:col-span-4"
            Icon={History}
            iconClass="text-[#6B9BFF] border-[#6B9BFF]/40"
            eyebrow="History & PRs"
            title="Every rep, immortalized."
            desc="Weekly workout history, top set per exercise, automatic 1RM (Epley) personal record detection, and progression charts."
            testId="bento-history"
          />

          <BentoCard
            index={9}
            className="md:col-span-2"
            Icon={TrendingUp}
            iconClass="text-[#7C3AED] border-[#7C3AED]/40"
            eyebrow="Adherence"
            title="Streaks visible."
            desc="Weekly completed vs planned, streak counters, 8-week trend."
            testId="bento-adherence"
          />

          <BentoCard
            index={10}
            className="md:col-span-4"
            Icon={Sparkles}
            iconClass="text-[#FFB020] border-[#FFB020]/40"
            eyebrow="Setup"
            title="60-second onboarding."
            desc="Wizard collects basics, builds your first plan instantly."
            testId="bento-onboarding"
          />
        </div>
      </div>
    </section>
  );
}
